import {
  Alert, Button, Card, Descriptions, Empty, Input, Modal, Space, Spin, Table, Tag, Typography,
} from 'antd';
import rideTripApi from 'api/rideTripApi';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CANCELLABLE_STATUSES, formatTripDate, formatTripMoney, getRideTripErrorMessage,
  PAYMENT_METHOD_LABELS, RideBookingStatusTag, RideTripStatusTag,
} from '../../rideTripsConstants';

const { Text, Title } = Typography;

function RideTripsDetailScreen() {
  const router = useRouter();
  const tripId = router.query?.tripId;
  const { user, permissionList } = useSelector((state) => state.auth);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const isAdmin = String(user?.role || '').toUpperCase() === 'ADMIN';
  const canManage = isAdmin || permissionList?.some((permission) => permission?.code === 'RIDE_TRIP_MANAGE');

  const loadDetail = useCallback(async () => {
    if (!tripId) return;
    setDetailLoading(true);
    try {
      const response = await rideTripApi.getTrip(tripId);
      setDetail(response?.trip || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRideTripErrorMessage(error));
    } finally {
      setDetailLoading(false);
    }
  }, [tripId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const openCancelModal = () => {
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const submitCancel = async () => {
    if (!tripId || cancelLoading) return;
    if (!cancelReason.trim()) return;
    setCancelLoading(true);
    try {
      const response = await rideTripApi.cancelTrip(tripId, cancelReason.trim());
      setDetail(response?.trip || null);
      setCancelModalOpen(false);
      setCancelReason('');
      ToastHelper.showSuccess('Đã hủy chuyến');
    } catch (error) {
      ToastHelper.showError(getRideTripErrorMessage(error));
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div>
      <div>
        {UIHelper.buildBreadcrumb([
          {
            title: 'Chuyến xe ghép',
            onPress: () => router.navigate(-1),
          },
          { title: detail ? `Chuyến ${detail.tripCode}` : 'Chi tiết chuyến' },
        ])}
      </div>
      <div className="container-fluid px-4 my-4">
        <Card>
          <Spin spinning={detailLoading}>
            {!detail && !detailLoading ? <Empty description="Không tải được chuyến đi" /> : detail && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <Title level={4} className="mb-0">
                    Chuyến
                    {' '}
                    {detail.tripCode}
                  </Title>
                  <Space>
                    <RideTripStatusTag status={detail.status} />
                    {canManage && CANCELLABLE_STATUSES.includes(detail.status) && (
                      <Button danger onClick={openCancelModal}>Hủy chuyến</Button>
                    )}
                  </Space>
                </div>

                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Tài xế">{detail.driver?.fullname || `User #${detail.driverId}`}</Descriptions.Item>
                  <Descriptions.Item label="Liên hệ tài xế">{detail.driver?.phone || detail.driver?.email || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Biển số">{detail.vehicle?.plate || `#${detail.vehicleId}`}</Descriptions.Item>
                  <Descriptions.Item label="Khởi hành">{formatTripDate(detail.departureAt)}</Descriptions.Item>
                  <Descriptions.Item label="Ghế">
                    {detail.seatsConfirmed || 0}
                    {' '}
                    đã đặt /
                    {' '}
                    {detail.seatsOffered || 0}
                    {' '}
                    mở bán
                  </Descriptions.Item>
                  {detail.isCharter && <Descriptions.Item label="Dịch vụ"><Tag color="gold">Bao xe {detail.charterVehicleCapacity || 4} chỗ</Tag></Descriptions.Item>}
                  <Descriptions.Item label={detail.isCharter ? 'Giá bao xe' : 'Giá mỗi ghế'}>{formatTripMoney(detail.isCharter ? detail.charterTotalFare : detail.pricePerSeat)}</Descriptions.Item>
                  <Descriptions.Item label="Quãng đường">
                    {detail.routeDistanceMeters == null ? '—' : `${(detail.routeDistanceMeters / 1000).toFixed(1)} km`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Thời lượng">
                    {detail.routeDurationSeconds == null ? '—' : `${Math.ceil(detail.routeDurationSeconds / 60)} phút`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Routing">
                    {detail.routingProvider || 'Chưa preview'}
                    {detail.routeMetrics?.approximation ? ' (ước tính)' : ''}
                  </Descriptions.Item>
                </Descriptions>

                <Card size="small" title="Điểm trên tuyến">
                  {(detail.stops || []).map((stop) => (
                    <div key={stop.tripStopId} className="mb-3">
                      <Text strong>
                        {stop.sequence + 1}
                        .
                        {' '}
                        {stop.address}
                      </Text>
                      <br />
                      <Text type="secondary">
                        {Number(stop.latitude).toFixed(6)}
                        ,
                        {' '}
                        {Number(stop.longitude).toFixed(6)}
                      </Text>
                    </div>
                  ))}
                </Card>

                <Card
                  size="small"
                  title={(
                    <>
                      Hành khách
                      {' '}
                      <Text type="secondary">
                        (
                        {(detail.bookings || []).length}
                        )
                      </Text>
                    </>
                  )}
                >
                  {(detail.bookings || []).length ? (
                    <Table
                      rowKey="bookingId"
                      size="small"
                      pagination={false}
                      scroll={{ x: 900 }}
                      dataSource={detail.bookings}
                      columns={[
                        {
                          title: 'Khách hàng',
                          render: (_, row) => (
                            <div>
                              <Text strong>{row.customer?.fullname || `User #${row.customerId}`}</Text>
                              <br />
                              <Text type="secondary">{row.customer?.phone || row.customer?.email || ''}</Text>
                            </div>
                          ),
                        },
                        {
                          title: 'Điểm đón / trả',
                          render: (_, row) => (
                            <div>
                              {row.pickupAddress}
                              <br />
                              <Text type="secondary">
                                →
                                {' '}
                                {row.dropoffAddress}
                              </Text>
                            </div>
                          ),
                        },
                        { title: 'Ghế', render: (_, row) => (row.isCharter ? <Tag color="gold">Bao xe {row.charterVehicleCapacity || 4} chỗ</Tag> : row.seats) },
                        { title: 'Tổng tiền', dataIndex: 'totalFare', render: formatTripMoney },
                        {
                          title: 'Thanh toán',
                          render: (_, row) => PAYMENT_METHOD_LABELS[row.paymentMethod] || row.paymentMethod,
                        },
                        { title: 'Trạng thái', dataIndex: 'status', render: (value) => <RideBookingStatusTag status={value} /> },
                      ]}
                    />
                  ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có khách đặt chỗ" />}
                </Card>

                {detail.status === 'CANCELLED' && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Chuyến đã bị hủy"
                    description={(
                      <>
                        <div>{detail.cancellationReason || 'Không có lý do'}</div>
                        <Text type="secondary">{formatTripDate(detail.cancelledAt)}</Text>
                      </>
                    )}
                  />
                )}
              </Space>
            )}
          </Spin>
        </Card>
      </div>

      <Modal
        title="Hủy chuyến xe ghép"
        open={cancelModalOpen}
        okText="Xác nhận hủy"
        cancelText="Đóng"
        okButtonProps={{ danger: true, disabled: !cancelReason.trim() }}
        confirmLoading={cancelLoading}
        onCancel={() => setCancelModalOpen(false)}
        onOk={submitCancel}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Hành động này không thể hoàn tác"
            description="Mọi yêu cầu đặt chỗ đang chờ hoặc đã xác nhận trên chuyến sẽ bị hủy, khách hàng đã thanh toán qua Ví EVG sẽ được hoàn tiền tự động. Tài xế và khách hàng đều sẽ nhận được thông báo."
          />
          <Input.TextArea
            rows={4}
            maxLength={500}
            showCount
            value={cancelReason}
            placeholder="Nhập lý do hủy chuyến để tài xế và khách hàng biết cần xử lý gì"
            onChange={(event) => setCancelReason(event.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
}

export default RideTripsDetailScreen;
